/**
 * Agregação cross-playbook das quick replies (Brief 4 · Gestor · Tela Quick Replies).
 *
 * Quick replies NÃO são recurso de primeira classe no backend: vivem embutidas
 * como Json dentro de cada Playbook (`Playbook.quickReplies`). Este catálogo é,
 * portanto, uma AGREGAÇÃO client-side sobre `GET /v1/playbooks` (D1) — cada linha
 * sabe de qual(is) playbook(s) veio. Lê o wire-shape duplo (`{ list: [...] }` vs
 * array puro) via `readList` (precedente Tela 1). Zero dado fabricado: categoria,
 * uso 30d, atendentes e detecção de ghost NÃO têm fonte no backend → ficam fora
 * (honest-defer · D3), nunca como placeholder enganoso.
 */

import { readList } from '../playbooks/playbook-labels';

import type { PlaybookDto, PlaybookQuickReply, PlaybookStatus } from '../playbooks/types';

/** Playbook que contém uma instância da quick reply (origem da linha do catálogo). */
export interface QuickReplyOwner {
  playbookId: string;
  playbookName: string;
  playbookVersion: number;
  playbookStatus: PlaybookStatus;
  /** id da quick reply dentro daquele playbook. */
  qrId: string;
}

/** Linha do catálogo: uma quick reply (por identidade) + os playbooks que a usam. */
export interface CrossQuickReply {
  /** Chave de identidade (label + texto + atalho normalizados). */
  key: string;
  label: string;
  text: string;
  shortcut?: string;
  /** Playbooks que contêm uma quick reply idêntica (derivável da agregação — D1). */
  owners: QuickReplyOwner[];
}

/**
 * Identidade de uma quick reply para consolidar instâncias idênticas entre
 * playbooks. Quick replies com mesmo rótulo/texto/atalho (após trim) viram uma só
 * linha com vários `owners` — base honesta da coluna "Playbooks que usam".
 */
function identityKey(qr: PlaybookQuickReply): string {
  return JSON.stringify([qr.label.trim(), qr.text.trim(), (qr.shortcut ?? '').trim()]);
}

/**
 * Achata as quick replies de todos os playbooks numa lista consolidada,
 * agrupando instâncias idênticas e preservando os playbooks de origem. Ordena
 * por rótulo (pt-BR) para estabilidade visual.
 */
export function aggregateQuickReplies(playbooks: PlaybookDto[]): CrossQuickReply[] {
  const byIdentity = new Map<string, CrossQuickReply>();

  for (const pb of playbooks) {
    const quickReplies = readList<PlaybookQuickReply>(pb.quickReplies);
    for (const qr of quickReplies) {
      const key = identityKey(qr);
      const owner: QuickReplyOwner = {
        playbookId: pb.id,
        playbookName: pb.name,
        playbookVersion: pb.version,
        playbookStatus: pb.status,
        qrId: qr.id,
      };
      const existing = byIdentity.get(key);
      if (existing !== undefined) {
        existing.owners.push(owner);
      } else {
        byIdentity.set(key, {
          key,
          label: qr.label,
          text: qr.text,
          ...(qr.shortcut !== undefined && qr.shortcut !== '' ? { shortcut: qr.shortcut } : {}),
          owners: [owner],
        });
      }
    }
  }

  return [...byIdentity.values()].sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
}

/** Filtra o catálogo por texto livre (rótulo · conteúdo · atalho · playbook de origem). */
export function filterQuickReplies(items: CrossQuickReply[], query: string): CrossQuickReply[] {
  const q = query.trim().toLowerCase();
  if (q === '') return items;
  return items.filter(
    (item) =>
      item.label.toLowerCase().includes(q) ||
      item.text.toLowerCase().includes(q) ||
      (item.shortcut ?? '').toLowerCase().includes(q) ||
      item.owners.some((o) => o.playbookName.toLowerCase().includes(q)),
  );
}
